import axiosInstance from './axiosInstance';

interface BillingTask {
  task_id: string;
  task_number: number | null;
  task_title: string;
  project_id: string;
  project_name: string;
  prompt_credit: number;
  cost_usd: number;
  completed_at: string | null;
  status: string;
}

export interface BillingSummary {
  total_completed_cost: number;
  total_completed_credits: number;
  task_count_completed: number;
  available_credits: number;
  last_month_credits: number;
}

interface ProjectTaskItem {
  task_id: string;
  task_number: number | null;
  task_title: string;
  prompt_credit: number;
  completed_at: string | null;
  status: string;
}

// Alias for backward compatibility
export type TaskDetail = ProjectTaskItem;

export interface ProjectGroup {
  project_id: string;
  project_name: string;
  tasks: ProjectTaskItem[];
  total_credits: number;
  estimated_credits: number;
  used_credits: number;
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total_tasks: number;
  total_pages: number;
}

export interface CreditLedgerPaginationInfo {
  page: number;
  page_size: number;
  total_entries: number;
  total_pages: number;
  has_next: boolean;
  is_estimated_total: boolean;
}

export interface TasksByProjectResponse {
  projects: ProjectGroup[];
  pagination: PaginationInfo;
  totals: {
    total_credits: number;
  };
}

export interface CreditLedgerEntry {
  id: string;
  amount: number;
  description: string;
  transaction_type: string;
  created_at: string;
  project_id: string | null;
}

export type CreditLedgerDirection = 'all' | 'credit' | 'debit';

export interface CreditLedgerHistoryResponse {
  entries: CreditLedgerEntry[];
  pagination: CreditLedgerPaginationInfo;
}

export interface GstDetails {
  gst_number: string | null;
  state_code: string | null;
  org_name: string | null;
}

const getHeaderNumber = (value: unknown): number | null => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

export const billingService = {
  getHistory: async (dateFrom?: string, dateTo?: string): Promise<BillingTask[]> => {
    const params: Record<string, string> = {};

    if (dateFrom) params.date_from = dateFrom;

    if (dateTo) params.date_to = dateTo;

    const response = await axiosInstance.get('/api/v1/billing/history', { params });

    return response.data;
  },

  getUpcoming: async (): Promise<BillingTask[]> => {
    const response = await axiosInstance.get('/api/v1/billing/upcoming');

    return response.data;
  },

  getSummary: async (): Promise<BillingSummary> => {
    const response = await axiosInstance.get('/api/v1/billing/summary');

    return response.data;
  },

  getTasksByProject: async (
    projectIds?: string[],
    page: number = 1,
    pageSize: number = 20,
    dateFrom?: string | null,
    dateTo?: string | null
  ): Promise<TasksByProjectResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };

    if (projectIds?.length) params.project_ids = projectIds.join(',');
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    const response = await axiosInstance.get('/api/v1/billing/tasks-by-project', { params });

    return response.data;
  },

  getLedgerHistory: async (
    page: number = 1,
    pageSize: number = 20,
    dateFrom?: string | null,
    dateTo?: string | null,
    direction: CreditLedgerDirection = 'all'
  ): Promise<CreditLedgerHistoryResponse> => {
    const offset = (page - 1) * pageSize;
    const params: Record<string, string | number> = { limit: pageSize + 1, offset, direction };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const response = await axiosInstance.get('/api/v1/billing/ledger', { params });
    const rawEntries = response.data as CreditLedgerEntry[];
    const entries = rawEntries.slice(0, pageSize);
    const exactTotalEntries = getHeaderNumber(response.headers['x-total-count']);
    const hasExtraEntry = rawEntries.length > pageSize;
    const loadedEntryCount = offset + entries.length;
    const hasNext = exactTotalEntries !== null ? loadedEntryCount < exactTotalEntries : hasExtraEntry;
    const totalEntries = exactTotalEntries ?? loadedEntryCount + (hasNext ? 1 : 0);
    const totalPages = exactTotalEntries !== null
      ? Math.max(1, Math.ceil(exactTotalEntries / pageSize))
      : Math.max(page, hasNext ? page + 1 : page);

    return {
      entries,
      pagination: {
        page,
        page_size: pageSize,
        total_entries: totalEntries,
        total_pages: totalPages,
        has_next: hasNext,
        is_estimated_total: exactTotalEntries === null,
      },
    };
  },

  getGstDetails: async (): Promise<GstDetails> => {
    const response = await axiosInstance.get('/api/v1/billing/gst-details');

    return response.data;
  },

  updateGstDetails: async (
    data: { gst_number?: string | null; org_name?: string | null; state_code?: string | null }
  ): Promise<GstDetails> => {
    const response = await axiosInstance.put('/api/v1/billing/gst-details', data);

    return response.data;
  },
};
